import React, { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { Button } from './ui/Button';
import { DownloadConfirmationModal } from './ui/DownloadConfirmationModal';
import { LoadingOverlay } from './ui/LoadingOverlay';
import { estimationService, EstimationResult } from '../services/estimationService';
import { coreService } from '../services/coreService';
import {
  CalculatorIcon,
  UsersIcon,
  BriefcaseIcon,
  ClockIcon,
  SparklesIcon,
  DownloadIcon,
  CheckCircleIcon,
  CpuIcon,
  LayoutIcon,
  TargetIcon,
  CalendarIcon,
  PrinterIcon,
  UploadIcon,
  ImageIcon,
  XIcon
} from 'lucide-react';
import toast from 'react-hot-toast';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Document, Packer, Paragraph, TextRun, HeadingLevel, Table, TableRow, TableCell, BorderStyle, WidthType, AlignmentType } from 'docx';

// Helper for saving with File System Access API
const saveFileWithPicker = async (blob: Blob, suggestedName: string, mimeType: string, extension: string) => {
  const toastId = toast.loading(`Preparing ${extension.toUpperCase()}...`);
  try {
    // @ts-ignore
    if (window.showSaveFilePicker) {
      try {
        // @ts-ignore
        const handle = await window.showSaveFilePicker({
          suggestedName,
          types: [{
            description: extension.toUpperCase() === '.DOC' ? 'Word Document' : 'PDF Document',
            accept: { [mimeType]: [extension] },
          }],
        });
        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();
        toast.success(`Saved successfully`, { id: toastId });
        return;
      } catch (err: any) {
        if (err.name === 'AbortError') {
          toast.dismiss(toastId);
          return;
        }
        console.warn('File System Picker failed, falling back to default download', err);
      }
    }

    // Fallback: Standard Download
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = suggestedName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 100);
    toast.success('Download started', { id: toastId });
  } catch (err: any) {
    console.error('Save Error:', err);
    toast.error('Failed to save file', { id: toastId });
  }
};

interface EstimationCalculatorProps {
  projectId: string;
  projectName: string;
  projectDescription: string;
  initialResult?: EstimationResult | null;
  initialConfig?: {
    developers?: number;
    qa?: number;
    experience?: 'Junior' | 'Mid' | 'Senior';
    duration?: string;
    techDescription?: string;
  } | null;
}

export const EstimationCalculator: React.FC<EstimationCalculatorProps> = ({
  projectId,
  projectName,
  projectDescription,
  initialResult = null,
  initialConfig = null,
}) => {
  const { user } = useSelector((state: RootState) => state.auth);
  const isPaid = user?.subscribed === 'S' || user?.plan === 'premier' || user?.plan === 'enterprise';

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<EstimationResult | null>(initialResult ?? null);
  const [isExportMenuOpen, setIsExportMenuOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Confirmation Modal and Logo states
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingDownload, setPendingDownload] = useState<(() => void) | null>(null);
  const [logo, setLogo] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Form states — initialized from saved config in DB
  const [developers, setDevelopers] = useState(initialConfig?.developers ?? 2);
  const [qa, setQa] = useState(initialConfig?.qa ?? 1);
  const [experience, setExperience] = useState<'Junior' | 'Mid' | 'Senior'>(initialConfig?.experience ?? 'Mid');
  const [duration, setDuration] = useState(initialConfig?.duration ?? '');
  const [techDescription, setTechDescription] = useState(initialConfig?.techDescription ?? '');
  const [isEnhancingTech, setIsEnhancingTech] = useState(false);

  // Sync state when project changes (e.g. user navigates between projects)
  useEffect(() => {
    setResult(initialResult ?? null);
    setDevelopers(initialConfig?.developers ?? 2);
    setQa(initialConfig?.qa ?? 1);
    setExperience(initialConfig?.experience ?? 'Mid');
    setDuration(initialConfig?.duration ?? '');
    setTechDescription(initialConfig?.techDescription ?? '');
  }, [projectId]);

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Logo should be less than 2MB');
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        const base64 = event.target?.result as string;
        setLogo(base64);
        toast.success('Logo uploaded and optimized');
      };
      reader.readAsDataURL(file);
    }
  };

  const triggerDownload = (downloadFn: () => void) => {
    if (isPaid) {
      downloadFn();
    } else {
      setPendingDownload(() => downloadFn);
      setShowConfirmModal(true);
    }
  };

  const handleConfirmDownload = () => {
    if (pendingDownload) {
      pendingDownload();
      setPendingDownload(null);
    }
    setShowConfirmModal(false);
  };

  const handleEnhanceTech = async () => {
    setIsEnhancingTech(true);
    try {
      const isGenerating = !techDescription.trim();

      const response = await coreService.enhance({
        content: techDescription || "No technology specified yet.",
        instruction: isGenerating
          ? "Recommend a comprehensive, modern, and scalable technology stack perfectly suited for this project's vision. Provide a professional, detailed architectural summary including frontend, backend, database, and deployment strategy."
          : "Expand this technology stack into a professional, detailed architectural description. Mention specific libraries, framework benefits, and deployment best practices relevant to these technologies.",
        context: {
          projectName,
          projectDescription
        }
      });

      if (response.success && response.data.editedContent) {
        setTechDescription(response.data.editedContent);
        toast.success(isGenerating ? 'AI Technology Recommendation success' : 'Architecture enhanced by AI');
      }
    } catch (err) {
      toast.error('Failed to process AI request');
    } finally {
      setIsEnhancingTech(false);
    }
  };

  const handleGenerate = async () => {
    setLoading(true);
    try {
      const response = await estimationService.generateEstimation(projectId, {
        developers,
        qa,
        experience,
        duration,
        techDescription
      });

      if (response.success && response.data && typeof response.data === 'object' && Object.keys(response.data).length > 0) {
        setResult(response.data);
        toast.success('Estimation generated successfully!');
      } else {
        toast.error(response.message || 'Failed to generate estimation');
        setResult(null);
      }
    } catch (error: any) {
      console.error(error);
      toast.error('An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleRecalculate = async () => {
    setResult(null);
    await estimationService.clearEstimation(projectId);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = async () => {
    if (!result) return;
    setIsExporting(true);
    setIsExportMenuOpen(false);

    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const marginX = 15;
      
      // Cover Page
      pdf.setFillColor(15, 23, 42); // Slate-900
      pdf.rect(0, 0, pageWidth, pageHeight, 'F');
      
      pdf.setDrawColor(249, 115, 22); // Orange
      pdf.setLineWidth(0.5);
      pdf.line(0, 50, pageWidth, 50);
      pdf.line(0, pageHeight - 50, pageWidth, pageHeight - 50);

      pdf.setTextColor(255, 255, 255);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(36);
      pdf.text('ESTIMATION', marginX, 120);
      pdf.setTextColor(249, 115, 22);
      pdf.text('BLUEPRINT', marginX, 135);

      pdf.setTextColor(203, 213, 225);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(14);
      pdf.text(projectName.toUpperCase(), marginX, 155);

      pdf.setFontSize(10);
      pdf.setTextColor(148, 163, 184);
      pdf.text(`Generated: ${new Date().toLocaleDateString()}`, marginX, pageHeight - 30);
      pdf.text('DocSprintAI Architecture Platform', marginX, pageHeight - 25);

      if (logo) {
        try {
          const logoSize = 40;
          pdf.addImage(logo, 'JPEG', pageWidth - marginX - logoSize, 15, logoSize, logoSize * 0.6, undefined, 'FAST');
        } catch (e) { console.warn('Logo add failed', e); }
      }

      // Add a function for page headers
      const addHeader = (doc: jsPDF, title: string) => {
        doc.setFillColor(248, 250, 252);
        doc.rect(0, 0, pageWidth, 20, 'F');
        doc.setTextColor(100, 116, 139);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.text(projectName.toUpperCase(), marginX, 12);
        doc.setTextColor(249, 115, 22);
        doc.text(title, pageWidth - marginX, 12, { align: 'right' });
      };

      // Content Pages
      pdf.addPage();
      addHeader(pdf, 'ESTIMATION REPORT');
      
      let y = 30;
      pdf.setTextColor(15, 23, 42);
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.text('1. Executive Summary', marginX, y);
      y += 10;
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(51, 65, 85);
      const splitOverview = pdf.splitTextToSize(result.overview, pageWidth - 2 * marginX);
      pdf.text(splitOverview, marginX, y);
      y += (splitOverview.length * 5) + 10;

      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(15, 23, 42);
      pdf.text('2. Strategic Project Scope', marginX, y);
      y += 10;
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(51, 65, 85);
      const splitScope = pdf.splitTextToSize(result.scope, pageWidth - 2 * marginX);
      pdf.text(splitScope, marginX, y);
      y += (splitScope.length * 5) + 15;

      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(15, 23, 42);
      pdf.text('3. Technical Architecture', marginX, y);
      y += 5;
      
      autoTable(pdf, {
        startY: y,
        head: [['Domain / Module', 'Recommended Technology']],
        body: result.techStack.map(item => [item.module, item.technology]),
        theme: 'grid',
        headStyles: { fillColor: [15, 23, 42], textColor: 255 },
        styles: { fontSize: 9, cellPadding: 4 },
        margin: { left: marginX, right: marginX }
      });
      
      y = (pdf as any).lastAutoTable.finalY + 15;

      // Ensure we don't break page right after Tech stack
      if (y > pageHeight - 40) {
        pdf.addPage();
        addHeader(pdf, 'ESTIMATION REPORT');
        y = 30;
      }

      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(15, 23, 42);
      pdf.text('4. Feature Specifications', marginX, y);
      y += 10;
      
      Object.entries(result.specifications).forEach(([role, specs]) => {
        if (y > pageHeight - 30) {
          pdf.addPage();
          addHeader(pdf, 'ESTIMATION REPORT');
          y = 30;
        }
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(249, 115, 22);
        pdf.text(`${role} Capabilities`, marginX, y);
        y += 7;
        
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(51, 65, 85);
        specs.forEach(spec => {
          if (y > pageHeight - 15) {
             pdf.addPage();
             addHeader(pdf, 'ESTIMATION REPORT');
             y = 30;
          }
          const splitSpec = pdf.splitTextToSize(`• ${spec}`, pageWidth - 2 * marginX);
          pdf.text(splitSpec, marginX, y);
          y += (splitSpec.length * 5) + 2;
        });
        y += 5;
      });

      pdf.addPage();
      addHeader(pdf, 'DEVELOPMENT ROADMAP');
      y = 30;

      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(15, 23, 42);
      pdf.text('5. Development Roadmap (Sprints)', marginX, y);
      y += 10;

      autoTable(pdf, {
        startY: y,
        head: [['Sprint', 'Focus', 'Key Tasks', 'Timeline']],
        body: result.sprints.map(s => [
          s.sprint,
          s.functionality,
          s.tasks.map(t => `• ${t}`).join('\n'),
          `${s.days} Days`
        ]),
        theme: 'grid',
        headStyles: { fillColor: [248, 250, 252], textColor: [15, 23, 42], fontStyle: 'bold' },
        styles: { fontSize: 9, cellPadding: 5 },
        columnStyles: { 0: { cellWidth: 20 }, 3: { cellWidth: 25, halign: 'center' } },
        margin: { left: marginX, right: marginX }
      });

      y = (pdf as any).lastAutoTable.finalY + 20;
      if (y > pageHeight - 60) {
        pdf.addPage();
        addHeader(pdf, 'EXECUTIVE SUMMARY');
        y = 30;
      }

      // Final Summary Box
      pdf.setFillColor(15, 23, 42);
      pdf.roundedRect(marginX, y, pageWidth - 2 * marginX, 60, 3, 3, 'F');
      
      pdf.setTextColor(249, 115, 22);
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Executive Summary & Readiness', marginX + 10, y + 15);
      
      pdf.setTextColor(203, 213, 225);
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Team Profile: ${developers} Developers, ${qa} QA (${experience} Level)`, marginX + 10, y + 25);
      pdf.text(`Testing & UAT: ${result.summary.testingUat}`, marginX + 10, y + 32);
      pdf.text(`Deployment Strategy: ${result.summary.deployment}`, marginX + 10, y + 39);
      
      pdf.setTextColor(249, 115, 22);
      pdf.setFontSize(24);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`${result.summary.totalDays} Work Days`, marginX + 10, y + 52);

      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 16);
      const pdfBlob = pdf.output('blob');
      await saveFileWithPicker(pdfBlob, `${projectName}_Estimation_${timestamp}.pdf`, 'application/pdf', '.pdf');

    } catch (error) {
      console.error('PDF Generation Error:', error);
      toast.error('Failed to generate PDF. Please try again.');
    } finally {
      const style = document.getElementById('pdf-export-style');
      if (style) style.remove();
      const container = document.getElementById('estimation-result');
      if (container) container.classList.remove('pdf-export-mode');
      setIsExporting(false);
    }
  };

  const handleDownloadDoc = async () => {
    if (!result) return;
    setIsExporting(true);
    setIsExportMenuOpen(false);

    try {
      const doc = new Document({
        creator: "DocSprintAI",
        title: `${projectName} - Estimation Report`,
        styles: {
          default: {
            document: {
              run: {
                font: "Calibri",
                size: 22,
                color: "333333",
              },
            },
          },
        },
        sections: [
          {
            properties: {},
            children: [
              new Paragraph({
                text: "Project Estimation Report",
                heading: HeadingLevel.HEADING_2,
                alignment: AlignmentType.CENTER,
              }),
              new Paragraph({
                text: projectName.toUpperCase(),
                heading: HeadingLevel.HEADING_1,
                alignment: AlignmentType.CENTER,
                spacing: { after: 400 },
              }),
              new Paragraph({
                text: `Generated: ${new Date().toLocaleDateString()}`,
                alignment: AlignmentType.CENTER,
                spacing: { after: 800 },
              }),
              new Paragraph({ text: "1. Executive Summary", heading: HeadingLevel.HEADING_2, spacing: { before: 400, after: 200 } }),
              new Paragraph({ text: result.overview, spacing: { after: 400 } }),
              
              new Paragraph({ text: "2. Strategic Project Scope", heading: HeadingLevel.HEADING_2, spacing: { before: 400, after: 200 } }),
              new Paragraph({ text: result.scope, spacing: { after: 400 } }),
              
              new Paragraph({ text: "3. Technical Architecture", heading: HeadingLevel.HEADING_2, spacing: { before: 400, after: 200 } }),
              new Table({
                width: { size: 100, type: WidthType.PERCENTAGE },
                rows: [
                  new TableRow({
                    children: [
                      new TableCell({ children: [new Paragraph({ text: "Domain / Module" })] }),
                      new TableCell({ children: [new Paragraph({ text: "Recommended Technology" })] }),
                    ],
                  }),
                  ...result.techStack.map(item => new TableRow({
                    children: [
                      new TableCell({ children: [new Paragraph(item.module)] }),
                      new TableCell({ children: [new Paragraph(item.technology)] }),
                    ],
                  }))
                ]
              }),
              
              new Paragraph({ text: "4. Feature Specifications", heading: HeadingLevel.HEADING_2, spacing: { before: 600, after: 200 } }),
              ...Object.entries(result.specifications).flatMap(([role, specs]) => [
                new Paragraph({ text: `${role} Capabilities`, heading: HeadingLevel.HEADING_3, spacing: { before: 200, after: 100 } }),
                ...specs.map(spec => new Paragraph({ text: spec, bullet: { level: 0 } }))
              ]),
              
              new Paragraph({ text: "5. Development Roadmap (Sprints)", heading: HeadingLevel.HEADING_2, spacing: { before: 600, after: 200 } }),
              new Table({
                width: { size: 100, type: WidthType.PERCENTAGE },
                rows: [
                  new TableRow({
                    children: [
                      new TableCell({ children: [new Paragraph({ text: "Sprint" })] }),
                      new TableCell({ children: [new Paragraph({ text: "Focus" })] }),
                      new TableCell({ children: [new Paragraph({ text: "Key Tasks" })] }),
                      new TableCell({ children: [new Paragraph({ text: "Timeline" })] }),
                    ],
                  }),
                  ...result.sprints.map(s => new TableRow({
                    children: [
                      new TableCell({ children: [new Paragraph(s.sprint)] }),
                      new TableCell({ children: [new Paragraph(s.functionality)] }),
                      new TableCell({ children: s.tasks.map(t => new Paragraph({ text: t, bullet: { level: 0 } })) }),
                      new TableCell({ children: [new Paragraph(`${s.days} Days`)] }),
                    ],
                  }))
                ]
              }),
              
              new Paragraph({ text: "Executive Summary & Readiness", heading: HeadingLevel.HEADING_2, spacing: { before: 600, after: 200 } }),
              new Paragraph({ text: `Team Profile: ${developers} Developers, ${qa} QA (${experience} Level)` }),
              new Paragraph({ text: `Testing & UAT: ${result.summary.testingUat}` }),
              new Paragraph({ text: `Deployment Strategy: ${result.summary.deployment}` }),
              new Paragraph({ text: `Total Project Duration: ${result.summary.totalDays} Work Days`, spacing: { before: 200 } }),
            ],
          },
        ],
      });

      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 16);
      const blob = await Packer.toBlob(doc);
      await saveFileWithPicker(blob, `${projectName}_Estimation_${timestamp}.docx`, 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', '.docx');
    } catch (error) {
      console.error('DOC Generation Error:', error);
      toast.error('Failed to generate Word document');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300 print:bg-white print:p-0">
      <LoadingOverlay 
        isOpen={isEnhancingTech} 
        message="Enhancing technology stack recommendations with AI..." 
      />
      <div className="flex justify-between items-center print:hidden">
        <div>
          <h1 className="text-xl font-bold text-white mb-1 flex items-center gap-2">
            Estimation Calculator
            <CalculatorIcon className="h-5 w-5 text-primary" />
          </h1>
          <p className="text-sm text-text-secondary">Calculate development timelines and resource allocation based on your team size.</p>
        </div>
        {result && (
          <div className="relative">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsExportMenuOpen(!isExportMenuOpen)}
              disabled={isExporting}
              className="flex items-center gap-2 border-white/10 text-white hover:bg-white/5 shadow-sm"
            >
              <DownloadIcon size={16} className={isExporting ? 'animate-bounce' : ''} />
              {isExporting ? 'Exporting...' : 'Export Report'}
            </Button>

            {isExportMenuOpen && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setIsExportMenuOpen(false)}
                />
                <div className="absolute right-0 mt-2 w-56 bg-[#131B2C]/90 backdrop-blur-xl rounded-xl shadow-2xl border border-white/10 z-20 py-2 animate-in fade-in zoom-in-95 duration-100">
                  <button
                    onClick={() => triggerDownload(handleDownloadPDF)}
                    className="w-full text-left px-4 py-3 text-sm text-white hover:bg-white/5 flex items-center gap-3 transition-colors"
                  >
                    <PrinterIcon size={16} className="text-orange-500" />
                    Save as PDF
                  </button>
                  <button
                    onClick={() => triggerDownload(handleDownloadDoc)}
                    className="w-full text-left px-4 py-3 text-sm text-white hover:bg-white/5 flex items-center gap-3 transition-colors"
                  >
                    <LayoutIcon size={16} className="text-blue-500" />
                    Save as Word (.doc)
                  </button>
                  <div className="border-t border-white/10 my-1" />
                  <button
                    onClick={handlePrint}
                    className="w-full text-left px-4 py-3 text-sm text-white/60 hover:bg-white/5 hover:text-white flex items-center gap-3 transition-colors"
                  >
                    <PrinterIcon size={16} />
                    System Print
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Input Form */}
      {!result ? (
        <div className="bg-[#131B2C]/40 backdrop-blur-xl border border-white/5 rounded-2xl p-8 shadow-2xl print:hidden">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            <div className="space-y-4">
              <label className="text-sm font-bold text-white flex items-center gap-2">
                <UsersIcon size={16} className="text-primary" />
                Total Developers
              </label>
              <input
                type="number"
                min={1}
                max={50}
                value={developers}
                onChange={(e) => setDevelopers(parseInt(e.target.value))}
                className="w-full bg-black/50 border border-white/10 text-white rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all"
              />
            </div>

            <div className="space-y-4">
              <label className="text-sm font-bold text-white flex items-center gap-2">
                <BriefcaseIcon size={16} className="text-primary" />
                QA Specialists
              </label>
              <input
                type="number"
                min={0}
                max={20}
                value={qa}
                onChange={(e) => setQa(parseInt(e.target.value))}
                className="w-full bg-black/50 border border-white/10 text-white rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all"
              />
            </div>

            <div className="space-y-4">
              <label className="text-sm font-bold text-white flex items-center gap-2">
                <CpuIcon size={16} className="text-primary" />
                Team Experience
              </label>
              <div className="flex gap-2">
                {['Junior', 'Mid', 'Senior'].map((exp) => (
                  <button
                    key={exp}
                    onClick={() => setExperience(exp as any)}
                    className={`flex-1 py-3 px-4 rounded-xl text-xs font-bold transition-all border ${experience === exp
                      ? 'bg-primary text-white border-primary shadow-[0_0_15px_rgba(255,107,0,0.3)] transform scale-105'
                      : 'bg-white/5 text-white/60 border-white/5 hover:border-white/20 hover:text-white'
                      }`}
                  >
                    {exp}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <label className="text-sm font-bold text-white flex items-center gap-2">
                  <ClockIcon size={16} className="text-primary" />
                  Desired Duration (Optional)
                </label>
              </div>
              <input
                type="text"
                placeholder="e.g. 3 months, 12 weeks"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                className="w-full bg-black/50 border border-white/10 text-white rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all"
              />
            </div>
          </div>

          <div className="space-y-4 mb-8">
            <div className="flex justify-between items-center">
              <label className="text-sm font-bold text-white flex items-center gap-2">
                <CpuIcon size={16} className="text-primary" />
                Technologies Description (Optional)
              </label>
              <button
                onClick={handleEnhanceTech}
                disabled={isEnhancingTech}
                className="text-xs font-bold tracking-wide text-primary hover:text-orange-400 flex items-center gap-1.5 bg-primary/10 px-3 py-1.5 rounded-lg border border-primary/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <SparklesIcon size={14} className={isEnhancingTech ? 'animate-spin' : ''} />
                {isEnhancingTech ? 'Architecting...' : !techDescription.trim() ? 'Suggest with AI' : 'Enhance with AI'}
              </button>
            </div>
            <textarea
              placeholder="e.g. React/Next.js for frontend, Node.js/PostgreSQL for backend, AWS for deployment..."
              value={techDescription}
              onChange={(e) => setTechDescription(e.target.value)}
              rows={8}
              className="w-full bg-black/50 border border-white/10 text-white rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all resize-y min-h-[200px]"
            />
          </div>
          <div className="bg-[#161F33] p-6 rounded-2xl border border-primary/20 flex items-start gap-4 mb-8">
            <div>
              <img src="/DocSprintLogo.png" alt="DocSprintAI" className="h-20 w-20 object-contain invert brightness-0 drop-shadow-[0_0_10px_rgba(255,107,0,0.4)]" />
            </div>
            <div>
              <h4 className="text-sm font-bold text-white mb-1">AI-Powered Estimation</h4>
              <p className="text-sm text-text-secondary leading-relaxed">
                DocSprintAI will analyze your project description and requirements to generate a realistic technical stack,
                feature specifications, and a sprint-wise development timeline tailored to your team configuration.
              </p>
            </div>
          </div>

          {/* Logo Upload - Paid Users Only */}
          {
            isPaid && (
              <div className="mb-8 pt-8 border-t border-white/10">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-bold text-white mb-1 flex items-center gap-2">
                      <ImageIcon size={16} className="text-primary" />
                      Brand Your Report
                    </h4>
                    <p className="text-sm text-text-secondary">Upload your company logo to display on the generated report.</p>
                  </div>
                  <div className="flex items-center gap-4">
                    {logo ? (
                      <div className="relative group">
                        <img src={logo} alt="Logo" className="w-16 h-16 object-contain rounded-lg border border-white/10 bg-black/50" />
                        <button
                          onClick={() => setLogo(null)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                        >
                          <XIcon size={12} />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="flex flex-col items-center justify-center w-16 h-16 border-2 border-dashed border-white/10 rounded-lg hover:border-primary hover:bg-primary/5 transition-all text-white/40 hover:text-primary"
                      >
                        <UploadIcon size={20} />
                        <span className="text-xs font-bold mt-1">Upload</span>
                      </button>
                    )}
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleLogoUpload}
                      accept="image/*"
                      className="hidden"
                    />
                  </div>
                </div>
              </div>
            )
          }

          <Button
            onClick={handleGenerate}
            loading={loading}
            className="w-full h-14 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-bold shadow-lg shadow-orange-500/20 text-lg flex items-center justify-center gap-3 transition-all hover:scale-[1.01]"
          >
            {!loading && <SparklesIcon size={20} />}
            Generate Sprint Wise Estimation
          </Button>
        </div >
      ) : (
        /* Result Display */
        <div id="estimation-result" className="space-y-12 pb-20 print:text-black">
          {/* Header Row */}
          <div className="flex justify-between items-start border-b border-white/10 pb-8 print:border-black">
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-white mb-2 tracking-tight">{projectName}</h1>
              <div className="flex gap-4">
                <span className="px-3 py-1 bg-primary/10 text-primary rounded border border-primary/20 text-xs font-bold uppercase tracking-wide print:hidden no-export">Project Plan</span>
                <span className="px-3 py-1 bg-white/5 text-white rounded border border-white/10 text-xs font-bold uppercase tracking-wide print:hidden no-export">{new Date().toLocaleDateString()}</span>
              </div>
            </div>

            <div className="flex flex-col items-end gap-2 min-w-[200px]">
              {logo && (
                <div className="h-28 w-48 flex items-center justify-end mb-2 no-export">
                  <img src={logo} alt="Company Logo" className="max-h-full max-w-full object-contain shadow-sm rounded-lg" />
                </div>
              )}
              <div className="text-right print:hidden no-export">
                <Button onClick={handleRecalculate} variant="outline" size="sm" className="mb-2">Recalculate</Button>
              </div>
            </div>
          </div>

          {/* Overview Section */}
          <section className="animate-in fade-in slide-in-from-left-4 duration-500">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-3 print:text-black">
              Overview
              <div className="h-[2px] flex-1 bg-white/10 print:bg-gray-200" />
            </h2>
            <div className="bg-[#131B2C]/40 backdrop-blur-xl p-8 rounded-2xl border border-white/5 shadow-sm leading-relaxed text-text-secondary print:border-none print:shadow-none print:p-0">
              {result.overview}
            </div>
          </section>

          {/* Scope Section */}
          <section className="animate-in fade-in slide-in-from-right-4 duration-500 delay-75">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-3 print:text-black">
              Scope
              <div className="h-[2px] flex-1 bg-white/10 print:bg-gray-200" />
            </h2>
            <div className="bg-[#131B2C]/40 backdrop-blur-xl p-8 rounded-2xl border border-white/5 shadow-sm leading-relaxed text-text-secondary print:border-none print:shadow-none print:p-0">
              {result.scope}
            </div>
          </section>

          {/* Tech Stack Table */}
          <section className="animate-in fade-in slide-in-from-bottom-4 duration-500 delay-100">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-3 print:text-black">
              Technical Stack
              <div className="h-[2px] flex-1 bg-white/10 print:bg-gray-200" />
            </h2>
            <div className="overflow-hidden rounded-2xl border border-white/5 print:border-black">
              <table className="w-full text-left border-collapse">
                <thead className="bg-[#161F33] border-b border-white/5 print:bg-gray-100">
                  <tr>
                    <th className="px-6 py-4 text-xs font-bold text-white uppercase tracking-wide print:text-black">Core Category</th>
                    <th className="px-6 py-4 text-xs font-bold text-white uppercase tracking-wide print:text-black">Selected Technology</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {result?.techStack?.map((item, idx) => (
                    <tr key={idx} className="bg-[#131B2C]/40 hover:bg-white/5 transition-colors">
                      <td className="px-6 py-4 text-sm font-bold text-white">{item.module}</td>
                      <td className="px-6 py-4 text-sm text-text-secondary">{item.technology}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Specifications */}
          <section className="animate-in fade-in slide-in-from-left-4 duration-500 delay-150">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-3 print:text-black">
              Application Specifications
              <div className="h-[2px] flex-1 bg-white/10 print:bg-gray-200" />
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 print:grid-cols-1">
              {Object.entries(result?.specifications || {}).map(([role, specs], idx) => (
                <div key={idx} className="bg-[#131B2C]/40 backdrop-blur-xl p-6 rounded-2xl border border-white/5 print:shadow-none print:border-none print:p-0">
                  <h3 className="text-sm font-bold text-white tracking-wide mb-4 flex items-center gap-2">
                    <span className="w-6 h-6 rounded flex items-center justify-center text-[10px] bg-primary/20 text-primary border border-primary/30 print:bg-black">
                      {idx + 1}
                    </span>
                    {role} Specifications
                  </h3>
                  <ul className="space-y-3">
                    {specs?.map((spec, sidx) => (
                      <li key={sidx} className="flex items-start gap-3 text-sm text-text-secondary">
                        <div className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 flex-shrink-0 print:bg-black" />
                        {spec}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>

          {/* Goals */}
          <section className="animate-in fade-in slide-in-from-right-4 duration-500 delay-200">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-3 print:text-black">
              Strategic Goals
              <div className="h-[2px] flex-1 bg-white/10 print:bg-gray-200" />
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {result?.goals?.map((goal, idx) => (
                <div key={idx} className="bg-[#161F33] p-6 rounded-2xl border border-primary/20 flex items-start gap-4 hover:shadow-[0_0_20px_rgba(255,107,0,0.1)] transition-shadow print:border-black print:bg-white">
                  <TargetIcon size={24} className="text-primary shrink-0 print:text-black" />
                  <p className="text-sm font-medium text-white print:text-black">{goal}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Sprints Table */}
          <section className="animate-in fade-in slide-in-from-bottom-4 duration-500 delay-300">
            <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-3 print:text-black">
              Sprints & Timelines
              <div className="h-[2px] flex-1 bg-white/10 print:bg-gray-200" />
            </h2>
            <div className="overflow-hidden rounded-2xl border border-white/5 print:border-black print:rounded-none">
              <table className="w-full text-left border-collapse">
                <thead className="bg-[#161F33] print:bg-gray-100 print:text-black">
                  <tr>
                    <th className="px-6 py-5 text-xs font-bold text-white uppercase tracking-wide print:text-black">Sprint</th>
                    <th className="px-6 py-5 text-xs font-bold text-white uppercase tracking-wide print:text-black">Functionality</th>
                    <th className="px-6 py-5 text-xs font-bold text-white uppercase tracking-wide print:text-black">Key Tasks</th>
                    <th className="px-6 py-5 text-xs font-bold text-white uppercase tracking-wide print:text-black">Timeline</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {result?.sprints?.map((s, idx) => (
                    <tr key={idx} className="bg-[#131B2C]/40 hover:bg-white/5 transition-colors">
                      <td className="px-6 py-6 vertical-top align-top">
                        <span className="px-3 py-1 bg-primary/10 text-primary rounded border border-primary/20 text-xs font-bold uppercase tracking-wide print:border-black print:text-black print:bg-white">
                          {s.sprint}
                        </span>
                      </td>
                      <td className="px-6 py-6 align-top">
                        <div className="text-sm font-bold text-white mb-1">{s.functionality}</div>
                        <div className="text-xs text-text-secondary font-bold uppercase tracking-wide">Module {idx + 1}</div>
                      </td>
                      <td className="px-6 py-6 align-top">
                        <ul className="space-y-2">
                          {s?.tasks?.map((task, tidx) => (
                            <li key={tidx} className="text-sm text-text-secondary flex items-start gap-2">
                              <CheckCircleIcon size={14} className="text-primary mt-0.5 shrink-0" />
                              {task}
                            </li>
                          ))}
                        </ul>
                      </td>
                      <td className="px-6 py-6 align-top">
                        <div className="flex flex-col items-center justify-center p-3 bg-black/30 rounded-xl border border-white/5 print:border-black">
                          <span className="text-xl font-bold text-white">{s.days}</span>
                          <span className="text-xs font-bold text-text-secondary uppercase">Days</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Summary Box */}
          <section className="animate-in zoom-in-95 duration-500 delay-500">
            <div className="bg-gradient-to-br from-primary to-orange-600 rounded-3xl p-10 text-white shadow-2xl shadow-primary/20 relative overflow-hidden print:bg-white print:text-black print:border-2 print:border-black print:shadow-none">
              {/* Abstract background shine */}
              <div className="absolute -top-24 -right-24 w-64 h-64 bg-white/10 rounded-full blur-3xl print:hidden"></div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-12 relative z-10 print:grid-cols-1 print:gap-4">
                <div className="space-y-2">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-orange-100 print:text-black">Testing & UAT</h4>
                  <p className="text-lg font-bold">{result?.summary?.testingUat}</p>
                </div>
                <div className="space-y-2">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-orange-100 print:text-black">Build & Deployment</h4>
                  <p className="text-lg font-bold">{result?.summary?.deployment}</p>
                </div>
                <div className="bg-black/30 backdrop-blur-md rounded-2xl p-6 flex flex-col items-center justify-center border border-white/20 print:bg-white print:border-black">
                  <h4 className="text-xs font-bold uppercase tracking-wider mb-2 print:text-black">Total Expected Days</h4>
                  <p className="text-4xl font-bold">{result?.summary?.totalDays} Days</p>
                </div>
              </div>

              <div className="mt-8 pt-8 border-t border-white/20 text-xs text-orange-100 flex items-start gap-2 print:border-black print:text-black">
                <span className="font-bold text-white print:text-black">Note:</span>
                These timelines are based on {developers} Developers and {qa} QA specialists with {experience} experience level. timelines may vary based on actual requirement complexity and project overheads.
              </div>
            </div>
          </section>

          {/* Footer print only */}
          <div className="hidden print:block text-center pt-8 text-[10px] text-gray-400">
            Generated by DocSprintAI - Innovation Architect Platform
          </div>
        </div>
      )
      }
      <DownloadConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => {
          setShowConfirmModal(false);
          setPendingDownload(null);
        }}
        onConfirm={handleConfirmDownload}
      />
    </div >
  );
};
